import { useState, useEffect, useMemo } from 'react';
import { useCart } from './CartContext';

const SUPABASE_FUNCTIONS_URL = 'https://fyxrbfcnzlrjjbubjzth.supabase.co/functions/v1';
const REFILL_BAG_PRODUCT_IDS: string[] = [
  'gid://shopify/Product/10307815833889',
];

interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  compareAtPrice: { amount: string; currencyCode: string } | null;
  availableForSale: boolean;
  quantityAvailable: number;
  selectedOptions: { name: string; value: string }[];
}

interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  featuredImage: { url: string; altText: string } | null;
  images: { edges: { node: { url: string; altText: string } }[] };
  variants: { edges: { node: ShopifyVariant }[] };
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  compareAtPriceRange: { minVariantPrice: { amount: string; currencyCode: string } };
}

export default function RefillBagsSection() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/shopify-refill-bags`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getProducts', productIds: REFILL_BAG_PRODUCT_IDS }),
        });
        const data = await res.json();
        const nodes: ShopifyProduct[] = (data?.data?.nodes ?? []).filter(Boolean);
        setProducts(nodes);
        if (nodes.length > 0) {
          const firstVariant = nodes[0].variants.edges[0]?.node;
          if (firstVariant) setSelectedVariantId(firstVariant.id);
        }
      } catch {
        // silently fall back
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const sizeOptions = useMemo(() => {
    const liveVariants = products.flatMap((prod) =>
      prod.variants.edges.map((e) => {
        const v = e.node;
        const sizeOpt = v.selectedOptions.find((o) => o.name.toLowerCase() === 'size');
        const label = sizeOpt?.value ?? v.title;
        const price = parseFloat(v.price.amount);
        const compareAt = v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : null;
        return {
          id: v.id,
          label,
          description: label.toLowerCase().includes('small')
            ? 'For dogs under 50 lbs (22kg)'
            : 'For dogs 50 lbs (22kg) and up',
          fits: label.toLowerCase().includes('small') ? 'Fits Small Scooper' : 'Fits Large Scooper',
          rolls: label.toLowerCase().includes('small') ? '2 rolls · 30 bags each' : '2 rolls · 20 bags each',
          price,
          compareAt,
          variantId: v.id,
        };
      })
    );

    if (liveVariants.length > 0) return liveVariants;

    return [
      { id: 'placeholder-small', label: 'Small', description: 'For dogs under 50 lbs (22kg)', fits: 'Fits Small Scooper', rolls: '2 rolls · 30 bags each', price: 0, compareAt: null, variantId: '' },
      { id: 'placeholder-large', label: 'Large', description: 'For dogs 50 lbs (22kg) and up', fits: 'Fits Large Scooper', rolls: '2 rolls · 20 bags each', price: 0, compareAt: null, variantId: '' },
    ];
  }, [products]);

  const currentSize = sizeOptions.find((s) => s.id === selectedVariantId) ?? sizeOptions[0];
  const price = currentSize?.price ?? 0;
  // Compare-at: Small $12→$16 (+$4), Large $16→$20 (+$4)
  const compareAt = price > 0 ? price + 4 : null;
  const hasLiveData = !!currentSize?.variantId;

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  const handleAddToCart = () => {
    if (!currentSize?.variantId) return;
    const imgUrl = products[0]?.featuredImage?.url ?? '';
    addItem({
      variantId: currentSize.variantId,
      productTitle: products[0]?.title ?? 'Refill Bags',
      variantTitle: currentSize.label,
      price,
      quantity,
      imageUrl: imgUrl,
      source: 'refill',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <section id="refill-bags" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#8A9B85] text-xs font-bold uppercase tracking-widest">Perfect Fit, Every Time</span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Refill Bags
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto">
            Designed to fit your scooper perfectly. Thick, leak-proof, and easy to load — just pick the size that matches your scooper.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Image Side */}
          <div className="w-full lg:w-1/2" data-product-shop>
            <div className="rounded-3xl overflow-hidden bg-[#F8F6F3] aspect-square flex items-center justify-center">
              {loading ? (
                <div className="w-full h-full bg-stone-200 animate-pulse"></div>
              ) : (
                <img
                  src={
                    products[0]?.featuredImage?.url ??
                    'https://public.readdy.ai/ai/img_res/edited_3e8f275146ce249e05dc0ed0ff3dd1bb_312e7d46.jpg'
                  }
                  alt="Refill Bags for Scooper"
                  className="w-full h-full object-cover object-top"
                />
              )}
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-3 mt-6">
              {[
                { icon: 'ri-shield-check-line', text: 'Leak-proof & thick' },
                { icon: 'ri-recycle-line', text: 'Eco-friendly material' },
                { icon: 'ri-focus-3-line', text: 'Perfect scooper fit' },
                { icon: 'ri-hand-heart-line', text: 'Easy to load' },
              ].map((feat) => (
                <div
                  key={feat.text}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8F6F3] border border-stone-100 text-stone-600 text-sm"
                >
                  <i className={`${feat.icon} text-[#8A9B85]`}></i>
                  {feat.text}
                </div>
              ))}
            </div>
          </div>

          {/* Purchase Side */}
          <div className="w-full lg:w-1/2 lg:sticky lg:top-24">
            <div className="bg-white rounded-3xl p-8 border border-stone-100">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-2xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {products[0]?.title ?? 'Refill Bags'}
                </h3>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-[#C4956A] text-sm"></i>
                  ))}
                </div>
              </div>
              <p className="text-stone-500 text-sm mb-6 leading-relaxed">
                {products[0]?.description ||
                  'Made to fit your scooper like a glove. Pick the size that matches your scooper — Small or Large — and never worry about bag compatibility again.'}
              </p>

              {/* Size Selector */}
              <div className="mb-6">
                <p className="text-stone-700 font-semibold text-sm mb-3">Select Size</p>
                {loading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-28 rounded-xl bg-stone-100 animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {sizeOptions.map((size) => {
                      const isActive = selectedVariantId === size.id || (!selectedVariantId && sizeOptions[0]?.id === size.id);
                      return (
                        <button
                          key={size.id}
                          onClick={() => setSelectedVariantId(size.id)}
                          className={`p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                            isActive ? 'border-[#8A9B85] bg-[#F4F7F4]' : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-stone-800 font-bold text-sm">{size.label}</span>
                            {isActive && (
                              <div className="w-4 h-4 flex items-center justify-center bg-[#8A9B85] rounded-full">
                                <i className="ri-check-line text-white text-[10px]"></i>
                              </div>
                            )}
                          </div>
                          <p className="text-stone-500 text-xs">{size.description}</p>
                          <p className="text-[#8A9B85] text-xs font-medium mt-1">{size.fits}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <p className="text-[#C4956A] font-bold text-sm">{size.price > 0 ? formatPrice(size.price) : '—'}</p>
                            {size.price > 0 && (
                              <p className="text-stone-400 text-xs line-through">{formatPrice(size.price + 4)}</p>
                            )}
                          </div>
                          <p className="text-stone-400 text-[10px] mt-0.5">{size.rolls}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <p className="text-stone-700 font-semibold text-sm mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-10 text-center text-stone-800 font-semibold text-sm">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <div>
                    {compareAt && compareAt > price && (
                      <span className="text-stone-400 text-sm line-through mr-2">{formatPrice(compareAt * quantity)}</span>
                    )}
                    <span className="text-2xl font-bold text-stone-800">{price > 0 ? formatPrice(price * quantity) : '—'}</span>
                  </div>
                </div>
              </div>

              {/* Compatibility note */}
              <div className="flex items-start gap-3 bg-[#F4F7F4] rounded-xl p-4 mb-6 border border-[#8A9B85]/20">
                <i className="ri-information-line text-[#8A9B85] text-lg mt-0.5 flex-shrink-0"></i>
                <p className="text-stone-600 text-xs leading-relaxed">
                  Make sure to select the <strong>same size</strong> as your scooper. Small bags fit the Small scooper, Large bags fit the Large scooper.
                </p>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={!hasLiveData}
                className={`w-full py-4 rounded-full font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  added
                    ? 'bg-[#8A9B85] text-white'
                    : !hasLiveData
                    ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    : 'bg-[#C4956A] hover:bg-[#b5845a] text-white'
                }`}
              >
                {added ? (
                  <><i className="ri-check-line text-lg"></i> Added to Cart!</>
                ) : (
                  <><i className="ri-shopping-bag-line text-lg"></i> Add to Cart{price > 0 ? ` — ${formatPrice(price * quantity)}` : ''}</>
                )}
              </button>

              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-stone-100">
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-truck-line text-[#8A9B85]"></i>
                  Free shipping $50+
                </div>
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-refresh-line text-[#8A9B85]"></i>
                  30-day returns
                </div>
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-shield-check-line text-[#8A9B85]"></i>
                  Secure checkout
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
